import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Context API — Theme & Auth State
//  Chapter 6 · State Management
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `// context/index.js — export all providers
export { ThemeProvider, useTheme } from './ThemeContext';
export { AuthProvider,  useAuth  } from './AuthContext';
export { CartProvider,  useCart  } from './CartContext';

// main.jsx — compose providers
root.render(
  <AuthProvider>
    <ThemeProvider>
      <CartProvider>
        <App />
      </CartProvider>
    </ThemeProvider>
  </AuthProvider>
);`;

const CODE_2 = `// context/ThemeContext.jsx
import { createContext, useContext, useState, useCallback } from 'react';

const ThemeContext = createContext(null);

const themes = {
  dark:  { bg: '#0A0C1E', text: '#FFFFFF', primary: '#61DAFB', card: '#1A1D35' },
  light: { bg: '#F8FAFC', text: '#1A202C', primary: '#0070F3', card: '#FFFFFF' },
};

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() =>
    localStorage.getItem('theme') || 'dark'
  );

  const theme = themes[mode];

  const toggleTheme = useCallback(() => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', next);
      return next;
    });
  }, []);

  const value = { mode, theme, toggleTheme, isDark: mode === 'dark' };

  return (
    <ThemeContext.Provider value={value}>
      <div style={{
        backgroundColor: theme.bg,
        color: theme.text,
        minHeight: '100vh',
        transition: 'background 0.3s, color 0.3s',
      }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be inside ThemeProvider');
  return ctx;
};`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ContextAPIThemeAndAuth() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 6 · State Management</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Context API — Theme & Auth State</h1>
        <p style={styles.desc}>Build scalable global state for theme and authentication using the Context API.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── When to Use Context API ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>When to Use Context API</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Context is ideal for <b>low-frequency, high-reach</b> data — values that many components need but don&#39;t change often. Perfect use cases:" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Current authenticated user" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• UI theme (dark/light)" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Language/locale" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Feature flags" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• App-wide settings" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Avoid Context for frequently changing state (list data, form inputs, counters) — it causes unnecessary re-renders." }} />
      </div>

      /* ── Multi-Context Architecture ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Multi-Context Architecture</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Use separate contexts for separate concerns — mixing everything in one context causes unnecessary re-renders." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Complete Theme Context ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Complete Theme Context</h2>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What is the performance concern with Context and how do you mitigate it?</div>
          <div style={styles.qItem}>✦ 2. How do you split a large context into smaller, focused ones?</div>
          <div style={styles.qItem}>✦ 3. Can you have multiple Providers for the same Context (nested)?</div>
          <div style={styles.qItem}>✦ 4. What is context selector and why would you need it?</div>
          <div style={styles.qItem}>✦ 5. Build a NotificationContext that queues and displays toast notifications app-wide.</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
